import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, unref as _unref, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "idle-view" }
const _hoisted_2 = { class: "stay-content" }
const _hoisted_3 = { class: "stay-header" }
const _hoisted_4 = { class: "stay-clock" }
const _hoisted_5 = { class: "status-badge" }

import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useCallStore } from '../stores/callStore'
import { signalingService } from '../services/signalingService'
import { apiClient } from '../services/apiClient'
import { useRoomId } from '../composables/useRoomId'
import type { IncomingCallEvent, BreakthroughCallEvent, CallEndedEvent } from '@virtual-care/shared'


export default /*@__PURE__*/_defineComponent({
  __name: 'IdleView',
  setup(__props) {

const router    = useRouter()
const callStore = useCallStore()
const { roomId, roomNumber } = useRoomId()

const isConnected = ref(false)
const currentTime = ref('')

function updateClock() {
  currentTime.value = new Date().toLocaleTimeString('en-US', {
    hour: '2-digit', minute: '2-digit', hour12: true,
  })
}

let clockInterval: ReturnType<typeof setInterval>

const handleIncomingCall = (event: IncomingCallEvent) => {
  callStore.setIncomingCall(
    event.sessionId,
    event.roomId,
    event.initiatorId,
    event.initiatorName,
    event.callPurpose,
    event.useGenericNurseLabel,
  )
  router.push(`/incoming/${event.sessionId}`)
}

const handleBreakthroughCall = (event: BreakthroughCallEvent) => {
  callStore.setBreakthroughCall(
    event.sessionId,
    event.roomId,
    event.initiatorId,
    event.initiatorName,
  )
  router.push(`/breakthrough/${event.sessionId}`)
}

// Prime audio context on page load so breakthrough calls can start audio
// This must happen early, before any automatic navigation
const primeAudioContext = async () => {
  if (typeof window === 'undefined') {
    console.log('[IdleView] Window not defined, skipping audio priming')
    return
  }

  try {
    const win = window as any
    const AudioContextCtor = win.AudioContext || win.webkitAudioContext
    if (!AudioContextCtor) {
      console.warn('[IdleView] AudioContext not supported')
      return
    }

    // Create shared audio context that persists across navigation
    if (!win.__vcCallAudioContext) {
      win.__vcCallAudioContext = new AudioContextCtor()
      console.log('[IdleView] Created shared audio context')
    }

    const ctx = win.__vcCallAudioContext as AudioContext
    console.log('[IdleView] Audio context state:', ctx.state)
    
    // Try to resume, but don't block on it - autoplay policy might prevent it
    if (ctx.state === 'suspended') {
      // Don't await - just trigger resume and let it fail silently if needed
      ctx.resume().catch((err) => {
        console.log('[IdleView] Audio context resume blocked by autoplay policy (expected):', err.message)
      })
    }

    // Try to play a silent tone (will also fail if no user gesture, but that's OK)
    try {
      const oscillator = ctx.createOscillator()
      const gain = ctx.createGain()
      gain.gain.value = 0.00001 // Nearly silent
      oscillator.connect(gain)
      gain.connect(ctx.destination)
      oscillator.start()
      oscillator.stop(ctx.currentTime + 0.01)
      console.log('[IdleView] Audio context primed successfully')
    } catch (err) {
      console.log('[IdleView] Silent tone blocked (expected on first load):', err)
    }
  } catch (error) {
    console.warn('[IdleView] Failed to prime audio context:', error)
    // Don't rethrow - audio priming is not critical for initial connection
  }
}

onMounted(async () => {
  try {
    console.log('[IdleView] Mounting, roomId:', roomId.value)
    
    updateClock()
    clockInterval = setInterval(updateClock, 1000)

    // Prime audio context EARLY before any calls come in
    console.log('[IdleView] Priming audio context...')
    await primeAudioContext()
    console.log('[IdleView] Audio priming complete')

    console.log('[IdleView] Connecting signaling service...')
    signalingService.connect(roomId.value)
    signalingService.on('incoming_call', handleIncomingCall)
    signalingService.on('breakthrough_call', handleBreakthroughCall)
    console.log('[IdleView] Signaling service connected')

    setTimeout(() => { 
      console.log('[IdleView] Connection status set to true')
      isConnected.value = true 
    }, 1000)
  } catch (error) {
    console.error('[IdleView] Error in onMounted:', error)
  }
})

onBeforeUnmount(() => {
  clearInterval(clockInterval)
  signalingService.off('incoming_call', handleIncomingCall)
  signalingService.off('breakthrough_call', handleBreakthroughCall)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[2] || (_cache[2] = _createElementVNode("div", { class: "idle-bg" }, null, -1)),
    _cache[3] || (_cache[3] = _createElementVNode("div", { class: "idle-bg-overlay" }, null, -1)),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[0] || (_cache[0] = _createElementVNode("span", { class: "stay-logo-text" }, "GetWell Network", -1)),
        _createElementVNode("span", _hoisted_4, _toDisplayString(currentTime.value), 1)
      ]),
      _cache[1] || (_cache[1] = _createElementVNode("div", { class: "stay-welcome" }, [
        _createElementVNode("p", { class: "stay-welcome-subtitle" }, "Welcome to"),
        _createElementVNode("h1", { class: "stay-welcome-title" }, "GetWell Stay")
      ], -1))
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("span", {
        class: _normalizeClass(["status-dot", { connected: isConnected.value }])
      }, null, 2),
      _createTextVNode(" Room " + _toDisplayString(_unref(roomNumber)), 1)
    ])
  ]))
}
}

})